/**
 * Site Adapter
 *
 * Converts API models to domain models, decoupling the application from the backend API structure.
 * This adapter ensures that changes to the API response format only require updates in one place.
 *
 * Requirements: 4.3, 4.4
 */

import type { SiteConfigResponse, SuperBayResponse } from './apiModels';
import type { FixedBay, Site, SuperBay } from './domainModels';

/**
 * Number of virtual (provisional) fixed bays generated for sites without
 * any Toptracer Go-enabled bays.
 */
export const MAX_PROVISIONAL_BAYS = 999;

/**
 * Generates virtual fixed bays with sequential IDs "1" through "999".
 * Each bay has a zeroed position and zero rotation.
 *
 * Requirements: 2.1, 2.2, 2.3
 */
function generateVirtualBays(): readonly FixedBay[] {
  return Array.from({ length: MAX_PROVISIONAL_BAYS }, (_, i) => ({
    id: String(i + 1),
    position: { x: 0, y: 0, z: 0 },
    rotationDegrees: 0,
  }));
}

/**
 * Sorts super bays by ID (numerically when IDs are numeric, lexicographically otherwise)
 * and assigns names. Named bays keep their name; unnamed bays receive
 * "Grass tee N" where N is a 1-based sequential index among unnamed bays in sorted order.
 * When there is only one unnamed bay, the suffix is omitted ("Grass tee").
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5
 */
export function computeSuperBayNames(
  superBays: readonly SuperBayResponse[]
): readonly SuperBay[] {
  const sorted = [...superBays].sort((a, b) => {
    const numA = Number(a.id);
    const numB = Number(b.id);
    if (Number.isFinite(numA) && Number.isFinite(numB)) {
      return numA - numB;
    }
    return a.id.localeCompare(b.id);
  });

  const unnamedCount = sorted.filter((bay) => !bay.name).length;
  let grassTeeIndex = 1;
  return sorted.map((bay) => ({
    id: bay.id,
    name: bay.name || (unnamedCount === 1 ? 'Grass tee' : `Grass tee ${grassTeeIndex++}`),
  }));
}


/**
 * Converts a SiteConfigResponse (API model) to a Site (domain model).
 *
 * Requirement 4.3: THE Site_Adapter SHALL convert SiteConfigResponse (API_Model) to Site (Domain_Model)
 * Requirement 4.4: THE Site_Adapter SHALL handle all fields from the API response
 * Requirements 1.2, 1.3: Detects virtual sites (no real bays) and sets isVirtual accordingly
 * Requirements 2.1–2.4: Injects virtual bays when isVirtual, preserves real bays otherwise
 *
 * @param response - The API response containing site configuration data
 * @returns A Site domain model with all fields mapped from the API response
 */
export function toSite(response: SiteConfigResponse): Site {
  const hasRealBays =
    response.bays.fixedBays.length > 0 || response.bays.superBays.length > 0;
  const isVirtual = !hasRealBays;

  return {
    siteId: response.siteId,
    name: response.name,
    isTournamentRange: response.isTournamentRange,
    bays: isVirtual
      ? { fixedBays: generateVirtualBays(), superBays: [] }
      : {
          fixedBays: response.bays.fixedBays.map((bay) => ({
            id: bay.id,
            position: { ...bay.position },
            rotationDegrees: bay.rotationDegrees,
          })),
          superBays: computeSuperBayNames(response.bays.superBays),
        },
    isDisabled: response.isDisabled,
    isVirtual,
    targets: (response.targets ?? []).map((target) => ({
      name: target.name,
      position: { ...target.position },
      colorHex: target.colorHex,
      isPositional: target.isPositional,
      isDirectional: target.isDirectional,
      targetType: target.targetType,
    })),
    disableOverNetWarning: response.disableOverNetWarning ?? false,
  };
}
